import { BlockStack, Box, Card, Divider, InlineGrid, InlineStack, Text, Tooltip } from '@shopify/polaris'
import type { BccData } from './useBccData'
import { CardHeader } from './LensBadge'

// MER, CAC and margin after ads have been computed by the Marketing Intelligence
// engine all along — they were simply never shown on the home page, which is the
// one place an owner looks before deciding whether to spend more (§9.3-12).

function egp(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000)     return `${Math.round(n / 1_000)}K`
  return Math.round(n).toLocaleString('en-US')
}

function Metric({
  label, value, sub, tone, help,
}: {
  label: string
  value: string
  sub?:  string
  tone?: 'success' | 'critical'
  help:  string
}) {
  return (
    <Tooltip content={help}>
      <Box background="bg-surface-secondary" padding="300" borderRadius="200">
        <BlockStack gap="100">
          <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
          <Text as="p" variant="headingMd" tone={tone}>{value}</Text>
          {sub && <Text as="p" variant="bodySm" tone="subdued">{sub}</Text>}
        </BlockStack>
      </Box>
    </Tooltip>
  )
}

function RoasTile({
  label, value, revenue, help, tone, strong,
}: {
  label:   string
  value:   number
  revenue: number
  help:    string
  tone?:   'success' | 'critical'
  strong?: boolean
}) {
  return (
    <Tooltip content={help}>
      <Box
        background={strong ? 'bg-surface-secondary' : 'bg-surface'}
        padding="300"
        borderRadius="200"
        borderColor="border"
        borderWidth="025"
      >
        <BlockStack gap="050">
          <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
          <Text as="p" variant="headingLg" tone={tone}>{`${value.toFixed(2)}×`}</Text>
          <Text as="p" variant="bodySm" tone="subdued">{`EGP ${egp(revenue)}`}</Text>
        </BlockStack>
      </Box>
    </Tooltip>
  )
}

type Props = { data: BccData }

export function MarketingEfficiency({ data }: Props) {
  const m    = data.marketing.metrics
  const roas = data.marketing.roasBreakdown

  // Break-even MER: every pound of revenue carries only the gross margin, so
  // spend pays for itself at 1 ÷ margin. Below that, more spend loses money.
  const grossMarginRate = m.netRevenue > 0 ? m.grossProfit / m.netRevenue : 0
  const breakEvenMer    = grossMarginRate > 0 ? 1 / grossMarginRate : null

  const merTone = breakEvenMer == null ? undefined
    : m.mer >= breakEvenMer * 1.2 ? 'success'
    : m.mer >= breakEvenMer       ? undefined
    : 'critical'

  const noSpend = m.totalAdSpend <= 0

  return (
    <Card>
      <BlockStack gap="400">
        <CardHeader title="Marketing Efficiency" lens="performance" maturity={data.dashboard.maturity} />

        {noSpend ? (
          <Text as="p" variant="bodySm" tone="caution">
            No marketing spend recorded for this period, so efficiency cannot be measured —
            and profit is overstated by whatever was actually spent.
          </Text>
        ) : (
          <>
            <InlineGrid columns={4} gap="200">
              <Metric
                label="Ad spend"
                value={`EGP ${egp(m.totalAdSpend)}`}
                help="Total marketing cost charged to this period's profit."
              />
              <Metric
                label="MER"
                value={`${m.mer.toFixed(2)}×`}
                sub={breakEvenMer != null ? `break-even ${breakEvenMer.toFixed(2)}×` : undefined}
                tone={merTone}
                help="Revenue divided by ad spend, blended across every channel. Per-channel ROAS needs revenue attribution, which the data does not carry yet."
              />
              <Metric
                label="CAC"
                value={m.cac != null ? `EGP ${egp(m.cac)}` : '—'}
                sub={`${m.newCustomers} new customers`}
                help="Ad spend divided by new customers acquired this period."
              />
              <Metric
                label="Margin after ads"
                value={`EGP ${egp(m.contributionMarginAfterAds)}`}
                sub={`${(m.cmAfterAdsRate * 100).toFixed(1)}% of revenue`}
                tone={m.contributionMarginAfterAds >= 0 ? 'success' : 'critical'}
                help="Gross profit less marketing cost — what is left to cover fixed expenses."
              />
            </InlineGrid>

            {/* The COD split: what the ad platform credits versus what survived
                delivery, and where it lands once the period finishes resolving. */}
            <Divider />

            <BlockStack gap="200">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingSm">Return on ad spend</Text>
                <Tooltip content="Share of this period's booked revenue whose delivery outcome is already known. The projection carries the rest.">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {`${Math.round(roas.revenueResolvedPct)}% resolved`}
                  </Text>
                </Tooltip>
              </InlineStack>

              <InlineGrid columns={3} gap="200">
                <RoasTile
                  label="Booked"
                  value={roas.bookedRoas}
                  revenue={roas.bookedRevenue}
                  help="Every order created this period, counted the way an ad platform counts it — at the moment of purchase, before delivery is attempted."
                />
                <RoasTile
                  label="Delivered"
                  value={roas.deliveredRoas}
                  revenue={roas.deliveredRevenue}
                  help="Only revenue that actually reached the customer. Reads low while the period is young, because recent orders have not had time to arrive."
                  tone={breakEvenMer != null && roas.deliveredRoas < breakEvenMer ? 'critical' : undefined}
                />
                <RoasTile
                  label="Projected"
                  value={roas.projectedRoas}
                  revenue={roas.deliveredRevenue + roas.openRevenue * (roas.resolvedSuccessRate / 100)}
                  help="Delivered revenue plus the share of still-open orders expected to arrive, at this period's own success rate. This is where the period lands if nothing changes."
                  tone={
                    breakEvenMer == null ? undefined
                      : roas.projectedRoas >= breakEvenMer ? 'success' : 'critical'
                  }
                  strong
                />
              </InlineGrid>

              {roas.failedRevenue > 0 && (
                <Text as="p" variant="bodySm" tone="subdued">
                  {`${egp(roas.failedRevenue)} EGP of booked revenue failed delivery — ${(100 - roas.resolvedSuccessRate).toFixed(0)}% of everything resolved so far.`}
                </Text>
              )}
            </BlockStack>

            {breakEvenMer != null && (
              <InlineStack align="space-between" blockAlign="center">
                <Text as="p" variant="bodySm" tone={merTone === 'critical' ? 'critical' : 'subdued'}>
                  {m.mer >= breakEvenMer
                    ? `Every EGP of ad spend returns ${m.mer.toFixed(2)} in revenue — above the ${breakEvenMer.toFixed(2)}× this margin needs to break even.`
                    : `Every EGP of ad spend returns ${m.mer.toFixed(2)} in revenue, below the ${breakEvenMer.toFixed(2)}× this margin needs. Spending more loses money at the current margin.`}
                </Text>
                {m.cac != null && m.avgOrderValue > 0 && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {`CAC is ${((m.cac / m.avgOrderValue) * 100).toFixed(0)}% of an average order`}
                  </Text>
                )}
              </InlineStack>
            )}
          </>
        )}
      </BlockStack>
    </Card>
  )
}
